import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { AuthStateResponse } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { ErrorLine } from '../../components/ErrorPanel';
import { Button, TextField } from '../../components/ui';
import { AuthLayout, useProceed } from './AuthLayout';

/** Second step: a code from the authenticator app, or a recovery code. */
export function TotpPage() {
  const proceed = useProceed();
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState('');
  const verify = useMutation({
    mutationFn: () =>
      api.post(useRecovery ? '/auth/recovery/verify' : '/auth/totp/verify', { body: { code }, schema: AuthStateResponse }),
    onSuccess: proceed,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    verify.mutate();
  };
  return (
    <AuthLayout title={t('auth.totpTitle')}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm">{t('auth.totpPrompt')}</p>
        <ErrorLine error={verify.error} />
        <TextField
          key={useRecovery ? 'recovery' : 'totp'}
          label={useRecovery ? t('auth.recoveryCode') : t('auth.totpCode')}
          inputMode={useRecovery ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          required
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <Button type="submit" variant="primary" className="w-full" disabled={verify.isPending}>
          {t('auth.verify')}
        </Button>
        <div className="flex flex-wrap justify-between gap-2 text-sm">
          <button
            type="button"
            className="text-brand underline"
            onClick={() => {
              setUseRecovery((v) => !v);
              setCode('');
              verify.reset();
            }}
          >
            {useRecovery ? t('auth.useAuthenticatorCode') : t('auth.useRecoveryCode')}
          </button>
          <Link className="text-brand underline" to="/login">
            {t('auth.backToSignIn')}
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
